import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ElementRect } from './hooks/useElementPosition';
import { EDITOR_SIDEBAR_WIDTH, EDITOR_HEADER_HEIGHT } from '@/constants/constants';

interface SpotlightProps {
  /** Target element rect */
  targetRect: ElementRect;
  /** Whether spotlight is visible */
  isVisible: boolean;
  /** Callback when overlay is clicked */
  onOverlayClick?: () => void;
  /** Whether clicking overlay should close tour */
  closeOnOverlayClick?: boolean;
  /** Overlay opacity (0-1) */
  overlayOpacity?: number;
  /** Border radius for the spotlight cutout */
  borderRadius?: number;
  /** Z-index for the spotlight */
  zIndex?: number;
  /** Allow interaction with the target element (for drag-drop steps) */
  allowTargetInteraction?: boolean;
  /** Area to apply spotlight overlay */
  spotlightArea?: 'full' | 'sidebar-only' | 'document-only';
}

/**
 * Spotlight component that creates a dark overlay with a cutout
 * around the target element using SVG clipPath
 */
export const Spotlight: React.FC<SpotlightProps> = ({
  targetRect,
  isVisible,
  onOverlayClick,
  closeOnOverlayClick = false,
  overlayOpacity = 0.75,
  borderRadius = 8,
  zIndex = 9998,
  allowTargetInteraction = false,
  spotlightArea = 'full',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && onOverlayClick) {
      onOverlayClick();
    }
    e.stopPropagation();
  };

  // Calculate if we have a valid target
  const hasValidTarget = targetRect.width > 0 && targetRect.height > 0;

  // For sidebar-only mode: only overlay the sidebar area, leave document visible
  // Allows drag-drop from sidebar to document
  if (spotlightArea === 'sidebar-only') {
    const sidebarWidth = EDITOR_SIDEBAR_WIDTH;

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: sidebarWidth,
              bottom: 0,
              zIndex,
              pointerEvents: allowTargetInteraction ? 'none' : 'auto',
              overflow: 'hidden',
            }}
          >
            <svg
              width={sidebarWidth}
              height="100%"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <defs>
                <mask id="spotlight-mask-sidebar">
                  <rect x="0" y="0" width={sidebarWidth} height="100%" fill="white" />
                  {hasValidTarget && (
                    <motion.rect
                      initial={{
                        x: targetRect.left,
                        y: targetRect.top,
                        width: targetRect.width,
                        height: targetRect.height,
                      }}
                      animate={{
                        x: targetRect.left,
                        y: targetRect.top,
                        width: targetRect.width,
                        height: targetRect.height,
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                      }}
                      rx={borderRadius}
                      ry={borderRadius}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>

              <rect
                x="0"
                y="0"
                width={sidebarWidth}
                height="100%"
                fill={`rgba(0, 0, 0, ${overlayOpacity})`}
                mask="url(#spotlight-mask-sidebar)"
              />
            </svg>

            {/* Highlight border around target */}
            {hasValidTarget && (
              <motion.div
                initial={{
                  top: targetRect.top,
                  left: targetRect.left,
                  width: targetRect.width,
                  height: targetRect.height,
                }}
                animate={{
                  top: targetRect.top,
                  left: targetRect.left,
                  width: targetRect.width,
                  height: targetRect.height,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                }}
                style={{
                  position: 'absolute',
                  borderRadius,
                  boxShadow: '0 0 0 4px rgba(0, 115, 234, 0.5)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Document-only mode: mask header and both sidebars, leave document visible
  // Target element still gets a cutout wherever it is
  if (spotlightArea === 'document-only') {
    const sidebarWidth = EDITOR_SIDEBAR_WIDTH;
    const headerHeight = EDITOR_HEADER_HEIGHT;
    const overlayColor = `rgba(0, 0, 0, ${overlayOpacity})`;

    // Calculate target position relative to each region's local coordinate system
    // Header: starts at (0, 0)
    const headerTargetX = targetRect.left;
    const headerTargetY = targetRect.top;

    // Left sidebar: starts at (0, headerHeight)
    const leftSidebarTargetX = targetRect.left;
    const leftSidebarTargetY = targetRect.top - headerHeight;

    // Right sidebar: starts at (viewportWidth - sidebarWidth, headerHeight)
    // For right sidebar, x needs to be relative to its left edge
    const rightSidebarTargetX = targetRect.left - (window.innerWidth - sidebarWidth);
    const rightSidebarTargetY = targetRect.top - headerHeight;

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex,
              pointerEvents: allowTargetInteraction ? 'none' : 'auto',
            }}
          >
            {/* Header overlay */}
            <svg
              width="100%"
              height={headerHeight}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <defs>
                <mask id="spotlight-mask-header">
                  <rect x="0" y="0" width="100%" height={headerHeight} fill="white" />
                  {hasValidTarget && (
                    <motion.rect
                      initial={{
                        x: headerTargetX,
                        y: headerTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      animate={{
                        x: headerTargetX,
                        y: headerTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      rx={borderRadius}
                      ry={borderRadius}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height={headerHeight}
                fill={overlayColor}
                mask="url(#spotlight-mask-header)"
              />
            </svg>

            {/* Left sidebar overlay (below header) */}
            <svg
              width={sidebarWidth}
              height={`calc(100% - ${headerHeight}px)`}
              style={{
                position: 'absolute',
                top: headerHeight,
                left: 0,
              }}
            >
              <defs>
                <mask id="spotlight-mask-left">
                  <rect x="0" y="0" width={sidebarWidth} height="100%" fill="white" />
                  {hasValidTarget && (
                    <motion.rect
                      initial={{
                        x: leftSidebarTargetX,
                        y: leftSidebarTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      animate={{
                        x: leftSidebarTargetX,
                        y: leftSidebarTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      rx={borderRadius}
                      ry={borderRadius}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width={sidebarWidth}
                height="100%"
                fill={overlayColor}
                mask="url(#spotlight-mask-left)"
              />
            </svg>

            {/* Right sidebar overlay (below header) */}
            <svg
              width={sidebarWidth}
              height={`calc(100% - ${headerHeight}px)`}
              style={{
                position: 'absolute',
                top: headerHeight,
                right: 0,
              }}
            >
              <defs>
                <mask id="spotlight-mask-right">
                  <rect x="0" y="0" width={sidebarWidth} height="100%" fill="white" />
                  {hasValidTarget && (
                    <motion.rect
                      initial={{
                        x: rightSidebarTargetX,
                        y: rightSidebarTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      animate={{
                        x: rightSidebarTargetX,
                        y: rightSidebarTargetY,
                        width: targetRect.width || 0,
                        height: targetRect.height || 0,
                      }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      rx={borderRadius}
                      ry={borderRadius}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width={sidebarWidth}
                height="100%"
                fill={overlayColor}
                mask="url(#spotlight-mask-right)"
              />
            </svg>

            {/* Highlight border around target */}
            {hasValidTarget && (
              <motion.div
                initial={{
                  top: targetRect.top,
                  left: targetRect.left,
                  width: targetRect.width,
                  height: targetRect.height,
                }}
                animate={{
                  top: targetRect.top,
                  left: targetRect.left,
                  width: targetRect.width,
                  height: targetRect.height,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                }}
                style={{
                  position: 'absolute',
                  borderRadius,
                  boxShadow: '0 0 0 4px rgba(0, 115, 234, 0.5)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Standard spotlight with SVG mask (blocks all pointer events)
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex,
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <svg
            width="100%"
            height="100%"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <defs>
              <mask id="spotlight-mask">
                {/* White background = visible overlay */}
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {/* Black rectangle = transparent cutout */}
                {hasValidTarget && (
                  <motion.rect
                    initial={{
                      x: targetRect.left,
                      y: targetRect.top,
                      width: targetRect.width,
                      height: targetRect.height,
                    }}
                    animate={{
                      x: targetRect.left,
                      y: targetRect.top,
                      width: targetRect.width,
                      height: targetRect.height,
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 30,
                    }}
                    rx={borderRadius}
                    ry={borderRadius}
                    fill="black"
                  />
                )}
              </mask>
            </defs>

            {/* Dark overlay with mask applied */}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill={`rgba(0, 0, 0, ${overlayOpacity})`}
              mask="url(#spotlight-mask)"
              style={{ pointerEvents: 'auto' }}
            />
          </svg>

          {/* Highlight border around target */}
          {hasValidTarget && (
            <motion.div
              initial={{
                top: targetRect.top,
                left: targetRect.left,
                width: targetRect.width,
                height: targetRect.height,
              }}
              animate={{
                top: targetRect.top,
                left: targetRect.left,
                width: targetRect.width,
                height: targetRect.height,
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              style={{
                position: 'absolute',
                borderRadius,
                boxShadow: '0 0 0 4px rgba(0, 115, 234, 0.5)',
                pointerEvents: 'none',
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Spotlight;
